import React, { useEffect, useState, useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface Props {
    evictionDataByYear: [string, number][];
    preventionDataByYear: [string, number][];
    onYearSelect: (year: string | null) => void;
    selectedRegion: string | null;
}

const colorScale = ["#8FA7C7", "#E2E8F0", "#9DBAD5", "#B7C8DB", "#98AFC7", "#C0D3E0"];


const EvictionsByYearBarChart = ({
    evictionDataByYear,
    preventionDataByYear,
    onYearSelect,
    selectedRegion
}: Props) => {
    const [yearSelected, setYearSelected] = useState<string | null>(null);
    const [selectedEvictions, setSelectedEvictions] = useState<number | null>(null);
    const [selectedPreventions, setSelectedPreventions] = useState<number | null>(null);
    const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);

    const [startIndex, setStartIndex] = useState(0);
    const windowSize = 9; // fixed 9 years
    const [allYears, setAllYears] = useState<string[]>([]);
    const hasInitialized = useRef(false);

    const formatNumber = (num: number) => {
        if (num >= 1_000_000) return (num / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
        if (num >= 1_000) return (num / 1_000).toFixed(0) + 'K';
        return num.toString();
    };

    const [chartData, setChartData] = useState<any>({
        labels: [],
        datasets: [],
    });

    useEffect(() => {
        let years = Array.from(
            new Set([
                ...evictionDataByYear.map(([year]) => year),
                ...preventionDataByYear.map(([year]) => year),
            ])
        ).sort();

        // ✅ Fail-safe: drop future years
        const currentYear = new Date().getFullYear();
        years = years.filter((y) => Number(y) <= currentYear);

        setAllYears(years);
    }, [evictionDataByYear, preventionDataByYear]);

    // Effect 2: set default window only once when allYears becomes available
    useEffect(() => {
        if (allYears.length > 0 && !hasInitialized.current) {
            const lastIndex = Math.max(0, allYears.length - windowSize);
            setStartIndex(lastIndex);
            hasInitialized.current = true; // prevents re-running
        }
    }, [allYears]);

    // Effect 3: update chartData when inputs change
    useEffect(() => {
        if (allYears.length === 0) return;

        const evictionMap = Object.fromEntries(evictionDataByYear);
        const preventionMap = Object.fromEntries(preventionDataByYear);

        const visibleYears = allYears.slice(startIndex, startIndex + windowSize);

        const evictionCounts = visibleYears.map((year) => evictionMap[year] || 0);
        const preventionCounts = visibleYears.map((year) => preventionMap[year] || 0);

        const getColorScale = (values: number[]) => {
            const min = Math.min(...values);
            const max = Math.max(...values);
            const steps = colorScale.length;
            const stepSize = (max - min) / steps || 1;
            return values.map((value) => {
                const index = Math.floor((value - min) / stepSize);
                return colorScale[Math.min(index, colorScale.length - 1)];
            });
        };

        setChartData({
            labels: visibleYears,
            datasets: [
                {
                    label: "Evictions",
                    data: evictionCounts,
                    backgroundColor: getColorScale(evictionCounts),
                    borderColor: "#fff",
                    borderWidth: 1,
                    borderRadius: 4,
                    hoverBackgroundColor: "rgba(0, 0, 0, 0.5)",
                    categoryPercentage: 0.8,
                    barPercentage: 0.6,
                },
                {
                    label: "Preventions",
                    data: preventionCounts,
                    backgroundColor: getColorScale(preventionCounts),
                    borderColor: "#fff",
                    borderWidth: 1,
                    borderRadius: 4,
                    hoverBackgroundColor: "rgba(0, 0, 0, 0.5)",
                    categoryPercentage: 0.8,
                    barPercentage: 0.6,
                },
            ],
        });
    }, [allYears, startIndex, evictionDataByYear, preventionDataByYear, selectedRegion]);


    const handleBarClick = (event: any, elements: any[]) => {
        if (elements.length > 0) {
            const { index } = elements[0];
            const year = chartData.labels[index];

            setYearSelected(year);
            setHighlightedIndex(index);

            const evictions = chartData.datasets[0].data[index];
            const preventions = chartData.datasets[1].data[index];

            setSelectedEvictions(evictions);
            setSelectedPreventions(preventions);
            onYearSelect(year);
        }
    };

    return (
        <div className="p-4 bg-white shadow-md rounded-md">
            {evictionDataByYear.length === 0 && preventionDataByYear.length === 0 ? (
                <div className="flex items-center justify-center h-[200px] gap-2 text-sm text-gray-500">
                    <svg
                        className="w-5 h-5 animate-spin text-blue-600"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                    >
                        <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                        />
                        <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        />
                    </svg>
                    Loading chart data...
                </div>
            ) : (
                <div className="relative w-full" style={{ height: "200px" }}>
                    {/* Chart */}
                    <Bar
                        data={chartData}
                        options={{
                            maintainAspectRatio: false,
                            responsive: true,
                            plugins: {
                                title: {
                                    display: true,
                                    text: selectedRegion
                                        ? `Evictions, Preventions (${selectedRegion})`
                                        : "Evictions, Preventions (All Regions)",
                                    font: { size: 12, weight: "bold" },
                                    padding: { top: 0, bottom: 0 },
                                    align: "center",
                                },
                                legend: {
                                    position: "top",
                                    labels: {
                                        boxWidth: 12,
                                        padding: 20,
                                        font: { size: 10 },
                                        usePointStyle: true,
                                        generateLabels: (chart) => {
                                            const original = ChartJS.defaults.plugins.legend.labels.generateLabels(chart)
                                            return original.map((labelItem) => {
                                                let newText = labelItem.text;

                                                if (yearSelected && selectedEvictions !== null && selectedPreventions !== null) {
                                                    if (labelItem.text === "Evictions") {
                                                        newText = `Evictions: ${selectedEvictions.toLocaleString()}`; // format with commas
                                                    }
                                                    if (labelItem.text === "Preventions") {
                                                        newText = `Preventions: ${selectedPreventions.toLocaleString()}`; // format with commas
                                                    }
                                                }

                                                return {
                                                    ...labelItem,
                                                    text: newText,
                                                };
                                            });
                                        }
                                    },
                                },
                                tooltip: {
                                    backgroundColor: "#fff",
                                    borderColor: "#ddd",
                                    borderWidth: 1,
                                    titleColor: "#333",
                                    bodyColor: "#333",
                                    padding: 10,
                                    callbacks: {
                                        label: (tooltipItem: any) =>
                                            `${tooltipItem.dataset.label}: ${tooltipItem.raw.toLocaleString()}`,
                                    },
                                },
                            },
                            scales: {
                                x: {
                                    grid: { display: false },
                                    ticks: {
                                        font: { size: 10 },
                                        autoSkip: false,
                                        maxRotation: 30,
                                        minRotation: 30,
                                    },
                                },
                                y: {
                                    beginAtZero: true,
                                    grid: { display: false },
                                    ticks: {
                                        font: { size: 10 },
                                        callback: (value) => formatNumber(value as number),
                                    },
                                },
                            },
                            onClick: handleBarClick,
                        }}
                    />

                    {/* Pagination controls below */}
                    <div className="absolute inset-x-0 -bottom-3 flex justify-between px-0">
                        {/* Prev Button */}
                        <button
                            className="w-6 h-6 flex items-center justify-center rounded-md bg-[#E5E7EB] hover:bg-[#D1D5DB] disabled:opacity-50"
                            onClick={() => setStartIndex(Math.max(0, startIndex - 1))}
                            disabled={startIndex === 0}
                        >
                            <div className="w-0 h-0 border-t-[6px] border-b-[6px] border-r-[6px] border-transparent border-r-[#374151]" />
                        </button>

                        {/* Next Button */}
                        <button
                            className="w-6 h-6 flex items-center justify-center rounded-md bg-[#E5E7EB] hover:bg-[#D1D5DB] disabled:opacity-50"
                            onClick={() => setStartIndex(startIndex + 1)}
                            disabled={startIndex >= allYears.length - windowSize}
                        >
                            <div className="w-0 h-0 border-t-[6px] border-b-[6px] border-l-[6px] border-transparent border-l-[#374151]" />
                        </button>
                    </div>

                </div>
            )}
        </div>
    );

};

export default EvictionsByYearBarChart;
